import { useState } from 'react';
import { Link, useParams } from 'react-router';
import type { ProgramTree, WorkoutRow } from '@st/contract';
import {
  useAddWeek,
  useAddWorkout,
  useAssignProgram,
  useDeleteWeek,
  useDeleteWorkout,
  useDuplicateWeek,
  useDuplicateWorkout,
  useProgramTree,
  useTrainerExercises,
} from '../../api/queries';
import { QueryBoundary } from '../../components/QueryBoundary';
import { PageHeader } from '../../components/PageHeader';
import { useTranslation } from '../../i18n/I18nProvider';
import { toMutationMessage } from '../../i18n/errorMessage';
import { DAY_KEYS, findWorkout } from '../../lib/program';
import { Button, ConfirmDialog, SkeletonCard, useToast } from '../../ui';
import { WeekColumn } from './builder/WeekColumn';
import { WorkoutEditor } from './builder/WorkoutEditor';
import { WorkoutFormModal } from './builder/WorkoutFormModal';
import { AssignProgramModal } from './builder/AssignProgramModal';

export function ProgramBuilder() {
  const { id = '' } = useParams();
  const { t } = useTranslation();
  const query = useProgramTree(id);

  return (
    <QueryBoundary
      query={query}
      skeleton={
        <div className="flex flex-col gap-4 lg:flex-row">
          <SkeletonCard className="lg:w-[19rem]" />
          <SkeletonCard className="lg:w-[19rem]" />
          <SkeletonCard className="hidden lg:block lg:w-[19rem]" />
        </div>
      }
    >
      {(tree) => <Builder programId={id} tree={tree} backLabel={t('builder.backToPrograms')} />}
    </QueryBoundary>
  );
}

function Builder({
  programId,
  tree,
  backLabel,
}: {
  programId: string;
  tree: ProgramTree;
  backLabel: string;
}) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const exercises = useTrainerExercises();

  const addWeek = useAddWeek(programId);
  const duplicateWeek = useDuplicateWeek(programId);
  const deleteWeek = useDeleteWeek(programId);
  const addWorkout = useAddWorkout(programId);
  const duplicateWorkout = useDuplicateWorkout(programId);
  const deleteWorkout = useDeleteWorkout(programId);
  const assign = useAssignProgram(programId);

  const [newWorkoutAt, setNewWorkoutAt] = useState<{ weekId: string; dayIndex: number } | null>(null);
  const [openWorkoutId, setOpenWorkoutId] = useState<string | null>(null);
  const [pendingWeek, setPendingWeek] = useState<{ id: string; title: string } | null>(null);
  const [pendingWorkout, setPendingWorkout] = useState<WorkoutRow | null>(null);
  const [assigning, setAssigning] = useState(false);

  // Stablo je jedini izvor istine: editor čita svježi trening iz njega, ne iz kopije.
  const openWorkout = openWorkoutId ? findWorkout(tree, openWorkoutId) : null;

  const busy =
    addWeek.isPending ||
    duplicateWeek.isPending ||
    deleteWeek.isPending ||
    addWorkout.isPending ||
    duplicateWorkout.isPending ||
    deleteWorkout.isPending;

  const fail = (conflictKey?: string) => (error: unknown) =>
    toast(toMutationMessage(t, error, conflictKey), 'danger');

  return (
    <>
      <p className="mb-2">
        <Link to="/trener/programi" className="text-[13px] text-ink-muted hover:text-ink">
          ← {backLabel}
        </Link>
      </p>

      <PageHeader
        eyebrow={t('builder.title')}
        title={tree.program.nameHr}
        {...(tree.program.descriptionHr ? { subtitle: tree.program.descriptionHr } : {})}
        action={
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="secondary" onClick={() => setAssigning(true)}>
              {t('builder.assign')}
            </Button>
            <Button
              size="sm"
              loading={addWeek.isPending}
              onClick={() => addWeek.mutate(undefined, { onError: fail() })}
            >
              {t('builder.addWeek')}
            </Button>
          </div>
        }
      />

      {tree.weeks.length === 0 ? (
        <div
          data-testid="empty-state"
          className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-line bg-surface px-6 py-12 text-center shadow-card"
        >
          <p className="max-w-prose text-[14.5px] leading-relaxed text-ink-muted">
            {t('builder.emptyProgram')}
          </p>
          <Button
            size="sm"
            variant="secondary"
            loading={addWeek.isPending}
            onClick={() => addWeek.mutate(undefined, { onError: fail() })}
          >
            {t('builder.addWeek')}
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-4 lg:flex-row lg:overflow-x-auto lg:pb-3">
          {tree.weeks.map((week, index) => (
            <WeekColumn
              key={week.id}
              week={week}
              index={index}
              busy={busy}
              onAddWorkout={(dayIndex) => setNewWorkoutAt({ weekId: week.id, dayIndex })}
              onOpenWorkout={(w) => setOpenWorkoutId(w.id)}
              onDuplicateWorkout={(w) =>
                duplicateWorkout.mutate(w.id, { onError: fail('builder.weekFull') })
              }
              onDeleteWorkout={setPendingWorkout}
              onDuplicateWeek={() =>
                duplicateWeek.mutate(week.id, {
                  onSuccess: () => toast(t('builder.weekDuplicated'), 'success'),
                  onError: fail(),
                })
              }
              onDeleteWeek={() =>
                setPendingWeek({
                  id: week.id,
                  title: week.labelHr ?? t('builder.week', { n: index + 1 }),
                })
              }
            />
          ))}
        </div>
      )}

      {newWorkoutAt && (
        <WorkoutFormModal
          open
          dayLabel={t(DAY_KEYS[newWorkoutAt.dayIndex] ?? '')}
          pending={addWorkout.isPending}
          onClose={() => setNewWorkoutAt(null)}
          onSubmit={(input) =>
            addWorkout.mutate(
              {
                weekId: newWorkoutAt.weekId,
                input: { ...input, dayIndex: newWorkoutAt.dayIndex },
              },
              {
                onSuccess: () => setNewWorkoutAt(null),
                onError: fail('builder.dayTaken'),
              },
            )
          }
        />
      )}

      {openWorkout && (
        <WorkoutEditor
          programId={programId}
          workout={openWorkout}
          library={exercises.data?.items ?? []}
          onClose={() => setOpenWorkoutId(null)}
        />
      )}

      <ConfirmDialog
        open={pendingWeek !== null}
        title={t('builder.deleteWeek')}
        question={t('builder.deleteWeekConfirm', { week: pendingWeek?.title ?? '' })}
        confirmLabel={t('action.confirmDelete')}
        cancelLabel={t('common.cancel')}
        closeLabel={t('a11y.closeDialog')}
        loading={deleteWeek.isPending}
        onClose={() => setPendingWeek(null)}
        onConfirm={() =>
          pendingWeek &&
          deleteWeek.mutate(pendingWeek.id, {
            onSuccess: () => setPendingWeek(null),
            onError: fail(),
          })
        }
      />

      <ConfirmDialog
        open={pendingWorkout !== null}
        title={t('builder.deleteWorkout')}
        question={t('builder.deleteWorkoutConfirm', { name: pendingWorkout?.nameHr ?? '' })}
        confirmLabel={t('action.confirmDelete')}
        cancelLabel={t('common.cancel')}
        closeLabel={t('a11y.closeDialog')}
        loading={deleteWorkout.isPending}
        onClose={() => setPendingWorkout(null)}
        onConfirm={() =>
          pendingWorkout &&
          deleteWorkout.mutate(pendingWorkout.id, {
            onSuccess: () => {
              if (openWorkoutId === pendingWorkout.id) setOpenWorkoutId(null);
              setPendingWorkout(null);
            },
            onError: fail(),
          })
        }
      />

      <AssignProgramModal
        open={assigning}
        programName={tree.program.nameHr}
        pending={assign.isPending}
        onClose={() => setAssigning(false)}
        onSubmit={(input) =>
          assign.mutate(input, {
            onSuccess: () => {
              setAssigning(false);
              toast(t('builder.assignDone'), 'success');
            },
            onError: fail(),
          })
        }
      />
    </>
  );
}
